interface PlaceholderSectionProps {
  id: string;
  title: string;
}

export default function PlaceholderSection({ id, title }: PlaceholderSectionProps) {
  // Special content for "Is Edbilities Right for You?" section
  if (id === 'who-its-for') {
    return (
      <>
        {/* "You're Not the Problem" Callout Block - Moved here */}
        <div className="py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-20">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-teal/5 via-teal/10 to-teal/5 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white rounded-3xl p-12 border-2 border-teal/20 shadow-xl shadow-teal/5">
                <div className="max-w-[900px] mx-auto text-center space-y-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-teal/10 rounded-full mx-auto mb-6">
                    <i className="ri-shield-check-line text-4xl text-teal"></i>
                  </div>
                  
                  <h3 className="font-sans text-3xl font-bold text-charcoal">
                    You're not the problem.
                  </h3>
                  
                  <p className="font-sans text-xl leading-relaxed text-warm-gray">
                    Most people were simply taught English in a way that doesn't lead to natural, professional speech.
                  </p>
                  
                  <p className="font-sans text-2xl leading-relaxed text-teal font-semibold">
                    Edbilities gives you a better method.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Original "Is Edbilities Right for You?" section */}
        <section id={id} className="py-24 bg-white relative overflow-hidden">
          {/* Subtle background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal/5 rounded-full blur-3xl"></div>
          
          <div className="max-w-[1200px] mx-auto px-20 relative z-10">
            <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
            
            {/* Opening Statement */}
            <div className="max-w-[900px] mx-auto mb-16">
              <p className="font-sans text-2xl leading-relaxed text-charcoal text-center font-medium">
                Edbilities is for professionals who speak English—but don't feel like their English carries the same power as their native language.
              </p>
            </div>

            {/* Context Paragraph */}
            <div className="max-w-[800px] mx-auto mb-20">
              <p className="font-sans text-xl leading-relaxed text-warm-gray text-center">
                You already communicate in English. You write emails, participate in meetings, and understand everything that's being said. But when you speak, your message doesn't always land the way you intend.
              </p>
            </div>

            {/* Professional Categories - Visual Cards */}
            <div className="mb-16">
              <p className="font-sans text-xl leading-relaxed text-charcoal text-center mb-12">
                Edbilities is built for professionals such as:
              </p>
              
              <div className="grid grid-cols-2 gap-6 max-w-[1000px] mx-auto">
                {/* Card 1 */}
                <div className="bg-warm-white rounded-2xl p-8 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-lg hover:shadow-teal/5">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal/10 rounded-xl mb-5">
                    <i className="ri-store-2-line text-2xl text-teal"></i>
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-charcoal mb-3">Business Owners</h3>
                  <p className="font-sans text-base leading-relaxed text-warm-gray">
                    Serving English-speaking customers and building credibility in competitive markets
                  </p>
                </div>

                {/* Card 2 */}
                <div className="bg-warm-white rounded-2xl p-8 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-lg hover:shadow-teal/5">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal/10 rounded-xl mb-5">
                    <i className="ri-heart-pulse-line text-2xl text-teal"></i>
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-charcoal mb-3">Medical & Clinical Professionals</h3>
                  <p className="font-sans text-base leading-relaxed text-warm-gray">
                    Building trust with patients through clear, confident communication
                  </p>
                </div>

                {/* Card 3 */}
                <div className="bg-warm-white rounded-2xl p-8 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-lg hover:shadow-teal/5">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal/10 rounded-xl mb-5">
                    <i className="ri-code-box-line text-2xl text-teal"></i>
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-charcoal mb-3">Engineers & Technical Consultants</h3>
                  <p className="font-sans text-base leading-relaxed text-warm-gray">
                    Working with clients and explaining complex concepts with clarity
                  </p>
                </div>

                {/* Card 4 */}
                <div className="bg-warm-white rounded-2xl p-8 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-lg hover:shadow-teal/5">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal/10 rounded-xl mb-5">
                    <i className="ri-line-chart-line text-2xl text-teal"></i>
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-charcoal mb-3">Sales & Service Professionals</h3>
                  <p className="font-sans text-base leading-relaxed text-warm-gray">
                    Expanding into English-speaking markets and closing deals with confidence
                  </p>
                </div>
              </div>
            </div>

            {/* Closing Statement */}
            <div className="max-w-[800px] mx-auto mt-20 pt-12 border-t border-gray-200">
              <p className="font-sans text-xl leading-relaxed text-charcoal text-center font-medium">
                If your accent or phrasing causes people to underestimate you—even when you're fully capable—this program is built for you.
              </p>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Special content for "The Problem" section
  if (id === 'the-problem') {
    return (
      <section id={id} className="py-20 bg-warm-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1200px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-16 text-center">Does this sound like you?</h2>
          
          <div className="flex items-start gap-16">
            {/* Left Column - Visual */}
            <div className="w-[40%] flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/94bcf525bb799db3b0f001f733dfb58c.jpg"
                  alt="Professional navigating a business conversation"
                  className="w-full h-auto object-cover object-top"
                  style={{ minHeight: '500px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-warm-white/20 to-transparent"></div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="w-[60%]">
              {/* Opening Statement */}
              <div className="mb-10">
                <p className="font-sans text-2xl leading-relaxed text-charcoal font-medium">
                  You are highly capable—but still lose opportunities when your English doesn't land the way you intend.
                </p>
              </div>

              {/* Subheading */}
              <div className="mb-6">
                <p className="font-sans text-lg text-warm-gray leading-relaxed">
                  This shows up in subtle but costly ways:
                </p>
              </div>

              {/* Problem List - Clean Bullet Format */}
              <div className="mb-12 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2.5 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    You get asked to repeat yourself on calls, even when your English is correct
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2.5 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    You know exactly what you want to say, but can't find the right words fast enough
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2.5 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    Your message takes 20 seconds instead of five—and you lose the room
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2.5 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    People hesitate or feel uncertain, even when you're fully qualified
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2.5 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    You succeed in your native language, but struggle to convert with English-speaking clients or customers
                  </p>
                </div>
              </div>

              {/* Closing Statements - Emphasized */}
              <div className="pt-10 border-t border-gray-200 space-y-5">
                <p className="font-sans text-xl leading-relaxed text-charcoal font-semibold">
                  This isn't about intelligence or capability.
                </p>
                <p className="font-sans text-lg leading-relaxed text-charcoal">
                  It's about whether your spoken English reflects your real professionalism and credentials.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "What Edbilities Helps You Do" section
  if (id === 'what-we-do') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1200px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Opening Statement */}
          <div className="max-w-[700px] mx-auto mb-20">
            <p className="font-sans text-2xl leading-relaxed text-charcoal text-center font-medium">
              Edbilities helps you speak English with:
            </p>
          </div>

          {/* Benefits Grid - 4 Cards */}
          <div className="grid grid-cols-2 gap-8 max-w-[1100px] mx-auto mb-20">
            {/* Card 1 - Clarity */}
            <div className="bg-warm-white rounded-2xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-2xl mb-6">
                <i className="ri-volume-up-line text-3xl text-teal"></i>
              </div>
              <h3 className="font-sans text-2xl font-semibold text-charcoal mb-4">Clarity</h3>
              <p className="font-sans text-lg leading-relaxed text-warm-gray">
                Be understood the first time
              </p>
            </div>

            {/* Card 2 - Professional Rhythm */}
            <div className="bg-warm-white rounded-2xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-2xl mb-6">
                <i className="ri-sound-module-line text-3xl text-teal"></i>
              </div>
              <h3 className="font-sans text-2xl font-semibold text-charcoal mb-4">Professional Rhythm and Flow</h3>
              <p className="font-sans text-lg leading-relaxed text-warm-gray">
                Emphasize the right words naturally
              </p>
            </div>

            {/* Card 3 - Stronger Vocabulary */}
            <div className="bg-warm-white rounded-2xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-2xl mb-6">
                <i className="ri-book-open-line text-3xl text-teal"></i>
              </div>
              <h3 className="font-sans text-2xl font-semibold text-charcoal mb-4">Stronger Vocabulary</h3>
              <p className="font-sans text-lg leading-relaxed text-warm-gray">
                Sound educated, precise, and current
              </p>
            </div>

            {/* Card 4 - Confidence */}
            <div className="bg-warm-white rounded-2xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-2xl mb-6">
                <i className="ri-user-star-line text-3xl text-teal"></i>
              </div>
              <h3 className="font-sans text-2xl font-semibold text-charcoal mb-4">Confidence in Real Situations</h3>
              <p className="font-sans text-lg leading-relaxed text-warm-gray">
                Calls, meetings, presentations, negotiations
              </p>
            </div>
          </div>

          {/* Closing Statements */}
          <div className="max-w-[800px] mx-auto pt-12 border-t border-gray-200 space-y-6">
            <p className="font-sans text-xl leading-relaxed text-charcoal text-center font-medium">
              The goal is not to erase who you are.
            </p>
            <p className="font-sans text-xl leading-relaxed text-charcoal text-center font-medium">
              It's to help your English sound natural, credible, and professional—so your expertise comes through.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "Why This Works When Other Courses Didn't" section
  if (id === 'why-it-works') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Hero Contrast Block - Full Width */}
          <div className="mb-20">
            <div className="grid grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl shadow-gray-900/10">
              {/* Left Panel - Traditional Approach */}
              <div className="bg-gray-50 p-12 relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gray-300"></div>
                <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded-2xl mb-6">
                  <i className="ri-book-2-line text-3xl text-gray-500"></i>
                </div>
                <h3 className="font-sans text-2xl font-bold text-gray-700 mb-4">Most Language Programs</h3>
                <p className="font-sans text-lg leading-relaxed text-gray-600">
                  Rules-based. They explain grammar and rules, then expect you to apply them on your own.
                </p>
              </div>

              {/* Right Panel - Edbilities Approach */}
              <div className="bg-gradient-to-br from-teal/10 via-teal/5 to-white p-12 relative border-l-4 border-teal">
                <div className="absolute top-0 left-0 w-full h-1 bg-teal"></div>
                <div className="w-16 h-16 flex items-center justify-center bg-teal/15 rounded-2xl mb-6">
                  <i className="ri-user-voice-line text-3xl text-teal"></i>
                </div>
                <h3 className="font-sans text-2xl font-bold text-charcoal mb-4">Edbilities is Different</h3>
                <p className="font-sans text-lg leading-relaxed text-charcoal font-semibold">
                  We build professional English through guided practice.
                </p>
              </div>
            </div>
          </div>

          {/* Three-Column Asymmetric Layout */}
          <div className="grid grid-cols-12 gap-8 mb-16">
            {/* Left Column - The Problem with Traditional Learning */}
            <div className="col-span-4 bg-warm-white rounded-2xl p-8 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl mb-5">
                <i className="ri-error-warning-line text-2xl text-gray-500"></i>
              </div>
              <h4 className="font-sans text-lg font-semibold text-charcoal mb-4">The Traditional Problem</h4>
              <p className="font-sans text-base leading-relaxed text-warm-gray mb-4">
                Many fluent speakers learned English word by word, then assembled sentences by translating from their native language.
              </p>
              <p className="font-sans text-base leading-relaxed text-charcoal font-medium">
                This leads people to invent English instead of discovering how it really works.
              </p>
            </div>

            {/* Middle Column - Our Method */}
            <div className="col-span-5 bg-teal/5 rounded-2xl p-8 border-2 border-teal/20">
              <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl mb-5">
                <i className="ri-lightbulb-line text-2xl text-teal"></i>
              </div>
              <h4 className="font-sans text-lg font-semibold text-charcoal mb-5">Instead of Teaching Rules in Isolation</h4>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    We listen closely to how you actually speak
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    We identify patterns that make fluent speech sound unnatural
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2 flex-shrink-0"></div>
                  <p className="font-sans text-base leading-relaxed text-charcoal">
                    We help you replace those patterns through repetition and real usage
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Real-Life Training */}
            <div className="col-span-3 bg-gradient-to-br from-teal to-teal/80 rounded-2xl p-8 text-white">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-5">
                <i className="ri-chat-voice-line text-2xl text-white"></i>
              </div>
              <h4 className="font-sans text-lg font-semibold mb-5">Real-Life Training</h4>
              
              <div className="space-y-3 text-sm">
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <p className="font-sans leading-relaxed">In full sentences, not fragments</p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <p className="font-sans leading-relaxed">Through listening and imitation</p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <p className="font-sans leading-relaxed">By breaking old habits</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "What We Mean by Professional English" section
  if (id === 'professional-english') {
    return (
      <section id={id} className="py-24 bg-gradient-to-b from-warm-white to-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-16 text-center">{title}</h2>
          
          {/* Opening Statement */}
          <div className="max-w-[900px] mx-auto mb-20 text-center">
            <p className="font-sans text-2xl leading-relaxed text-charcoal font-medium mb-3">
              Professional English is more than pronunciation.
            </p>
            <p className="font-sans text-xl leading-relaxed text-warm-gray">
              It's how educated, capable people actually communicate in real-world settings.
            </p>
          </div>

          {/* Subheading */}
          <div className="max-w-[700px] mx-auto mb-16 text-center">
            <p className="font-sans text-lg text-charcoal font-semibold">
              Edbilities focuses on four core areas:
            </p>
          </div>

          {/* Four Core Areas - 2x2 Grid Framework */}
          <div className="grid grid-cols-2 gap-8 max-w-[1200px] mx-auto">
            {/* Card 1 - Accent Building */}
            <div className="bg-white rounded-3xl p-10 border-2 border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/5 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal/15 to-teal/5 rounded-2xl mb-6">
                  <i className="ri-mic-line text-3xl text-teal"></i>
                </div>
                <div className="mb-5">
                  <h3 className="font-sans text-2xl font-bold text-charcoal">Accent Building</h3>
                </div>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  Not to eliminate identity—but to develop a natural, intelligible, professional sound that reflects your education and credentials.
                </p>
              </div>
            </div>

            {/* Card 2 - Breaking Non-Native Speech Habits */}
            <div className="bg-white rounded-3xl p-10 border-2 border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/5 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal/15 to-teal/5 rounded-2xl mb-6">
                  <i className="ri-refresh-line text-3xl text-teal"></i>
                </div>
                <div className="mb-5">
                  <h3 className="font-sans text-2xl font-bold text-charcoal">Breaking Non-Native Speech Habits</h3>
                </div>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  Many fluent speakers apply sentence patterns from their native language. We help you identify and replace habits that make English sound unnatural—even when it's technically correct.
                </p>
              </div>
            </div>

            {/* Card 3 - Advanced Vocabulary and Register */}
            <div className="bg-white rounded-3xl p-10 border-2 border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/5 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal/15 to-teal/5 rounded-2xl mb-6">
                  <i className="ri-text text-3xl text-teal"></i>
                </div>
                <div className="mb-5">
                  <h3 className="font-sans text-2xl font-bold text-charcoal">Advanced Vocabulary and Register</h3>
                </div>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  English operates on levels. We help you move beyond basic vocabulary and use more precise, powerful language appropriate for professional and academic contexts.
                </p>
              </div>
            </div>

            {/* Card 4 - Connotation, Nuance, and Depth */}
            <div className="bg-white rounded-3xl p-10 border-2 border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/5 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal/15 to-teal/5 rounded-2xl mb-6">
                  <i className="ri-focus-3-line text-3xl text-teal"></i>
                </div>
                <div className="mb-5">
                  <h3 className="font-sans text-2xl font-bold text-charcoal">Connotation, Nuance, and Depth</h3>
                </div>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  Learn how meaning changes with context—idioms, phrasal verbs, emphasis, and everyday spoken language that textbooks rarely teach.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "How It Works" section
  if (id === 'how-it-works') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Horizontal 3-Step Diagram */}
          <div className="grid grid-cols-3 gap-12 mb-20">
            {/* Step 1 */}
            <div className="relative">
              {/* Step Number */}
              <div className="mb-8">
                <span className="font-sans text-6xl font-bold text-teal/20">01</span>
              </div>
              
              {/* Step Content */}
              <div>
                <h3 className="font-sans text-2xl font-semibold text-charcoal mb-5 leading-tight">
                  Enroll + Get Your Materials
                </h3>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  You get self-paced lessons and practice tools designed for professional speaking—not textbook English.
                </p>
              </div>

              {/* Connector Arrow */}
              <div className="absolute top-12 -right-6 w-12 h-0.5 bg-gray-200 hidden xl:block">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gray-300"></div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              {/* Step Number */}
              <div className="mb-8">
                <span className="font-sans text-6xl font-bold text-teal/20">02</span>
              </div>
              
              {/* Step Content */}
              <div>
                <h3 className="font-sans text-2xl font-semibold text-charcoal mb-5 leading-tight">
                  Practice with Structure
                </h3>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  You train rhythm, flow, tone, vocabulary, and sentence patterns in short sessions that fit a busy schedule.
                </p>
              </div>

              {/* Connector Arrow */}
              <div className="absolute top-12 -right-6 w-12 h-0.5 bg-gray-200 hidden xl:block">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gray-300"></div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              {/* Step Number */}
              <div className="mb-8">
                <span className="font-sans text-6xl font-bold text-teal/20">03</span>
              </div>
              
              {/* Step Content */}
              <div>
                <h3 className="font-sans text-2xl font-semibold text-charcoal mb-5 leading-tight">
                  Join Weekly Live Group Sessions
                </h3>
                <p className="font-sans text-base leading-relaxed text-warm-gray">
                  Each week, you can join live group sessions with Tony (your coach). Bring real scenarios—calls, presentations, scripts—and get corrections you can apply immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Outcome Block */}
          <div className="mt-20 pt-16 border-t-2 border-gray-200">
            <div className="bg-gradient-to-r from-teal/5 via-teal/10 to-teal/5 rounded-2xl p-12 text-center">
              <div className="max-w-[700px] mx-auto">
                <p className="font-sans text-sm uppercase tracking-wider text-teal font-semibold mb-4">
                  Outcome
                </p>
                <p className="font-sans text-2xl leading-relaxed text-charcoal font-medium">
                  In 12 weeks, your English becomes clearer, more natural, and more persuasive in the situations that matter.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "What You Can Expect to Change" section
  if (id === 'expect-change') {
    return (
      <section id={id} className="py-24 bg-warm-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Top Block - What People Notice */}
          <div className="mb-20">
            <div className="flex items-center gap-16">
              {/* Left - Image */}
              <div className="w-[45%]">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Two%20diverse%20business%20professionals%20having%20an%20engaged%20one-on-one%20conversation%20in%20a%20modern%20bright%20office%20with%20natural%20lighting.%20One%20person%20is%20speaking%20confidently%20and%20clearly%20while%20the%20other%20is%20listening%20attentively%20with%20a%20warm%20smile%20showing%20understanding%20and%20connection.%20The%20scene%20captures%20authentic%20professional%20communication%20with%20genuine%20facial%20expressions%20of%20comprehension%20and%20trust.%20Natural%20warm%20tones%2C%20slightly%20desaturated%20colors%2C%20neutral%20background.%20The%20atmosphere%20conveys%20mutual%20respect%2C%20clear%20communication%2C%20and%20professional%20rapport%20between%20colleagues%20in%20a%20real%20workplace%20setting.&width=700&height=600&seq=edbilities-expect-change-conversation-v1&orientation=landscape"
                    alt="Professional conversation showing clear communication"
                    className="w-full h-auto object-cover object-top"
                    style={{ minHeight: '500px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="w-[55%]">
                <div className="mb-10">
                  <p className="font-sans text-2xl leading-relaxed text-charcoal font-semibold">
                    Clients and colleagues should notice things like:
                  </p>
                </div>

                {/* Quote-style Cards */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 border-l-4 border-teal shadow-sm hover:shadow-md transition-all duration-200">
                    <p className="font-sans text-lg leading-relaxed text-charcoal italic">
                      "I can understand you instantly now."
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-l-4 border-teal shadow-sm hover:shadow-md transition-all duration-200">
                    <p className="font-sans text-lg leading-relaxed text-charcoal italic">
                      "You sound more confident and polished."
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-l-4 border-teal shadow-sm hover:shadow-md transition-all duration-200">
                    <p className="font-sans text-lg leading-relaxed text-charcoal italic">
                      "Your message is clear and direct."
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-l-4 border-teal shadow-sm hover:shadow-md transition-all duration-200">
                    <p className="font-sans text-lg leading-relaxed text-charcoal italic">
                      "You come across as credible and senior."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Block - Professional Impact */}
          <div className="mt-24">
            <div className="flex items-center gap-16">
              {/* Left - Content */}
              <div className="w-[55%]">
                <div className="mb-10">
                  <p className="font-sans text-2xl leading-relaxed text-charcoal font-semibold">
                    The practical professional impact:
                  </p>
                </div>

                {/* Impact Cards */}
                <div className="space-y-5">
                  <div className="bg-gradient-to-r from-teal/5 to-transparent rounded-xl p-7 border border-teal/20">
                    <div className="flex items-start gap-4">
                      <div className="w-3 h-3 rounded-full bg-teal mt-1.5 flex-shrink-0"></div>
                      <p className="font-sans text-lg leading-relaxed text-charcoal">
                        Fewer lost opportunities due to communication friction
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-teal/5 to-transparent rounded-xl p-7 border border-teal/20">
                    <div className="flex items-start gap-4">
                      <div className="w-3 h-3 rounded-full bg-teal mt-1.5 flex-shrink-0"></div>
                      <p className="font-sans text-lg leading-relaxed text-charcoal">
                        Stronger trust in high-stakes conversations
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-teal/5 to-transparent rounded-xl p-7 border border-teal/20">
                    <div className="flex items-start gap-4">
                      <div className="w-3 h-3 rounded-full bg-teal mt-1.5 flex-shrink-0"></div>
                      <p className="font-sans text-lg leading-relaxed text-charcoal">
                        More conversions, better outcomes, higher earnings
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Image */}
              <div className="w-[45%]">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20confident%20diverse%20professional%20leading%20a%20small%20group%20meeting%20or%20presentation%20in%20a%20modern%20bright%20office%20space%20with%20natural%20lighting.%20The%20speaker%20is%20gesturing%20naturally%20while%20explaining%20something%20important%2C%20and%20three%20colleagues%20are%20listening%20attentively%20showing%20engagement%20and%20understanding.%20The%20scene%20captures%20authentic%20workplace%20collaboration%20with%20genuine%20expressions%20of%20focus%2C%20respect%2C%20and%20professional%20confidence.%20Natural%20warm%20tones%2C%20slightly%20desaturated%20colors%2C%20soft%20focus%20background.%20The%20atmosphere%20conveys%20leadership%2C%20clear%20communication%2C%20and%20successful%20professional%20interaction%20in%20a%20real%20business%20setting.&width=700&height=600&seq=edbilities-expect-change-meeting-v1&orientation=landscape"
                    alt="Professional leading a confident meeting"
                    className="w-full h-auto object-cover object-top"
                    style={{ minHeight: '500px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "Testimonials" section
  if (id === 'testimonials') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1200px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Three Testimonial Cards */}
          <div className="grid grid-cols-3 gap-10 max-w-[1300px] mx-auto">
            {/* Testimonial 1 - Maria S. */}
            <div className="bg-warm-white rounded-3xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              {/* Portrait */}
              <div className="mb-8">
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto border-4 border-white shadow-lg shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20woman%20in%20her%20mid-30s%20with%20warm%20smile%20and%20thoughtful%20expression%2C%20wearing%20business%20casual%20attire.%20Natural%20lighting%2C%20neutral%20soft%20background%2C%20authentic%20and%20approachable%20demeanor.%20Healthcare%20professional%20appearance%20with%20calm%20confidence%20and%20genuine%20warmth.%20Slightly%20desaturated%20colors%2C%20natural%20skin%20tones%2C%20professional%20headshot%20style%20but%20not%20overly%20polished.%20Real%20person%20aesthetic%2C%20not%20stock%20model%20pose.&width=400&height=400&seq=edbilities-testimonial-maria-healthcare-v1&orientation=squarish"
                    alt="Maria S., Healthcare Professional"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Quote */}
              <div className="mb-8">
                <p className="font-sans text-lg leading-relaxed text-charcoal text-center">
                  "I was already fluent, but my English didn't sound professional. After Edbilities, my calls are smoother and people trust me right away."
                </p>
              </div>

              {/* Attribution */}
              <div className="text-center pt-6 border-t border-gray-200">
                <p className="font-sans text-base font-semibold text-charcoal mb-1">Maria S.</p>
                <p className="font-sans text-sm text-warm-gray">Healthcare Professional</p>
              </div>
            </div>

            {/* Testimonial 2 - Daniel R. */}
            <div className="bg-warm-white rounded-3xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              {/* Portrait */}
              <div className="mb-8">
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto border-4 border-white shadow-lg shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20man%20in%20his%20early%2040s%20with%20calm%20assured%20expression%2C%20wearing%20business%20attire.%20Natural%20lighting%2C%20neutral%20soft%20background%2C%20authentic%20and%20credible%20demeanor.%20Insurance%20sales%20professional%20appearance%20with%20approachable%20confidence%20and%20genuine%20presence.%20Slightly%20desaturated%20colors%2C%20natural%20skin%20tones%2C%20professional%20headshot%20style%20but%20not%20overly%20polished.%20Real%20person%20aesthetic%2C%20not%20stock%20model%20pose.&width=400&height=400&seq=edbilities-testimonial-daniel-sales-v1&orientation=squarish"
                    alt="Daniel R., Insurance Sales"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Quote */}
              <div className="mb-8">
                <p className="font-sans text-lg leading-relaxed text-charcoal text-center">
                  "I used to sell mainly within my native-language market. Now I can sell confidently to English-speaking customers."
                </p>
              </div>

              {/* Attribution */}
              <div className="text-center pt-6 border-t border-gray-200">
                <p className="font-sans text-base font-semibold text-charcoal mb-1">Daniel R.</p>
                <p className="font-sans text-sm text-warm-gray">Insurance Sales</p>
              </div>
            </div>

            {/* Testimonial 3 - Alexei K. */}
            <div className="bg-warm-white rounded-3xl p-10 border border-gray-100 hover:border-teal/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal/5">
              {/* Portrait */}
              <div className="mb-8">
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto border-4 border-white shadow-lg shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20thoughtful%20man%20in%20his%20late%2030s%20with%20intelligent%20focused%20expression%2C%20wearing%20smart%20casual%20business%20attire.%20Natural%20lighting%2C%20neutral%20soft%20background%2C%20authentic%20and%20credible%20demeanor.%20Technology%20consultant%20appearance%20with%20calm%20confidence%20and%20analytical%20presence.%20Slightly%20desaturated%20colors%2C%20natural%20skin%20tones%2C%20professional%20headshot%20style%20but%20not%20overly%20polished.%20Real%20person%20aesthetic%2C%20not%20stock%20model%20pose.&width=400&height=400&seq=edbilities-testimonial-alexei-tech-v1&orientation=squarish"
                    alt="Alexei K., Technology Consultant"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Quote */}
              <div className="mb-8">
                <p className="font-sans text-lg leading-relaxed text-charcoal text-center">
                  "I didn't need more grammar. I needed my English to sound natural, credible, and sharp. This finally did it."
                </p>
              </div>

              {/* Attribution */}
              <div className="text-center pt-6 border-t border-gray-200">
                <p className="font-sans text-base font-semibold text-charcoal mb-1">Alexei K.</p>
                <p className="font-sans text-sm text-warm-gray">Technology Consultant</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "What You Get" section
  if (id === 'offer') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1200px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          <div className="flex items-start gap-16">
            {/* Left Column - Program Details */}
            <div className="w-[60%]">
              {/* Main Offer Card */}
              <div className="bg-gradient-to-br from-warm-white to-white rounded-3xl p-12 border-2 border-gray-100 shadow-xl shadow-gray-900/5">
                {/* Program Name */}
                <div className="mb-10 pb-8 border-b-2 border-gray-200">
                  <h3 className="font-sans text-3xl font-bold text-charcoal">
                    Edbilities Professional English
                  </h3>
                </div>

                {/* Program Details Grid */}
                <div className="space-y-6 mb-10">
                  {/* Focus */}
                  <div className="flex items-start gap-5">
                    <div className="w-32 flex-shrink-0">
                      <p className="font-sans text-sm uppercase tracking-wider text-warm-gray font-semibold">
                        Focus
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-lg text-charcoal">
                        Accent + Flow + Vocabulary + Real-World Speaking
                      </p>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-start gap-5">
                    <div className="w-32 flex-shrink-0">
                      <p className="font-sans text-sm uppercase tracking-wider text-warm-gray font-semibold">
                        Duration
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-lg text-charcoal">
                        12 weeks
                      </p>
                    </div>
                  </div>

                  {/* Format */}
                  <div className="flex items-start gap-5">
                    <div className="w-32 flex-shrink-0">
                      <p className="font-sans text-sm uppercase tracking-wider text-warm-gray font-semibold">
                        Format
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-lg text-charcoal">
                        Self-paced lessons + weekly live group sessions
                      </p>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="flex items-start gap-5">
                    <div className="w-32 flex-shrink-0">
                      <p className="font-sans text-sm uppercase tracking-wider text-warm-gray font-semibold">
                        Schedule
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-lg text-charcoal">
                        Flexible — designed for busy professionals
                      </p>
                    </div>
                  </div>
                </div>

                {/* Investment */}
                <div className="mt-10 pt-8 border-t-2 border-gray-200">
                  <div className="flex items-baseline gap-3">
                    <p className="font-sans text-sm uppercase tracking-wider text-warm-gray font-semibold">
                      Investment
                    </p>
                    <p className="font-sans text-4xl font-bold text-teal">
                      $995
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Is Edbilities Right for You? + Image */}
            <div className="w-[40%]">
              {/* Is Edbilities Right for You? Card */}
              <div className="bg-teal/5 rounded-2xl p-8 border border-teal/20 mb-8">
                <h4 className="font-sans text-lg font-semibold text-charcoal mb-5">
                  Designed for:
                </h4>
                <p className="font-sans text-base leading-relaxed text-charcoal">
                  Fluent non-native English speakers who want their spoken English to sound natural, credible, and professional in real business situations.
                </p>
              </div>

              {/* Supportive Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-gray-900/10">
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20diverse%20professionals%20collaborating%20at%20a%20modern%20workspace%2C%20one%20person%20explaining%20something%20with%20confidence%20while%20the%20other%20listens%20attentively%20and%20nods%20in%20understanding.%20Natural%20office%20lighting%2C%20warm%20professional%20atmosphere%2C%20authentic%20workplace%20interaction%20showing%20clear%20communication%20and%20mutual%20respect.%20Slightly%20desaturated%20colors%2C%20natural%20tones%2C%20soft%20focus%20background.%20The%20scene%20captures%20professional%20collaboration%20and%20effective%20communication%20in%20a%20real%20business%20setting%20with%20calm%20confidence%20and%20genuine%20engagement.&width=600&height=500&seq=edbilities-offer-collaboration-v1&orientation=landscape"
                  alt="Professional collaboration and communication"
                  className="w-full h-auto object-cover object-top"
                  style={{ minHeight: '400px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "FAQs" section
  if (id === 'faqs') {
    return (
      <section id={id} className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-[900px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* FAQ Accordion */}
          <div className="space-y-6">
            {/* FAQ 1 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "I took an accent course before and it didn't work. Why will this?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  Most programs teach rules and hope you apply them later. Edbilities is practice-driven: you build new habits through guided repetition, real listening, and correction.
                </p>
              </div>
            </details>

            {/* FAQ 2 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "What if I'm just not good at languages?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  You're not the problem. Most people were taught English in a way that creates long-term speaking habits. Edbilities gives you a better method—one you can actually apply.
                </p>
              </div>
            </details>

            {/* FAQ 3 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "How much time does this take?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  It's designed for busy schedules. You'll make progress with short, consistent practice sessions. Once you enroll, we'll recommend a simple weekly plan that fits your calendar.
                </p>
              </div>
            </details>

            {/* FAQ 4 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "Will I lose my accent entirely?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  No. We build a professional English accent that sounds natural and credible. The goal is clarity, flow, and confident professional delivery—not erasing who you are.
                </p>
              </div>
            </details>

            {/* FAQ 5 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "Is this only about accent?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  No. Accent is one part. We focus on professional speaking: rhythm, flow, vocabulary choice, and sentence patterns that sound natural in professional American English.
                </p>
              </div>
            </details>

            {/* FAQ 6 */}
            <details className="group bg-warm-white rounded-2xl border border-gray-100 hover:border-teal/20 transition-all duration-200">
              <summary className="flex items-center justify-between cursor-pointer p-8 list-none">
                <h3 className="font-sans text-xl font-semibold text-charcoal pr-8">
                  "Will I get live support?"
                </h3>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal/10 group-open:bg-teal transition-all duration-200">
                  <i className="ri-add-line text-xl text-teal group-open:text-white group-open:rotate-45 transition-all duration-200"></i>
                </div>
              </summary>
              <div className="px-8 pb-8 pt-2">
                <p className="font-sans text-lg leading-relaxed text-warm-gray">
                  Yes. Weekly live group sessions are included, so you can ask questions and get real corrections.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "Meet the Team" section
  if (id === 'team') {
    return (
      <section id={id} className="py-24 bg-warm-white relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-20 relative z-10">
          <h2 className="font-serif text-5xl text-charcoal mb-20 text-center">{title}</h2>
          
          {/* Team Member 1 - Tony Patterson */}
          <div className="mb-24">
            <div className="flex items-start gap-16">
              {/* Portrait */}
              <div className="w-[35%] flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20male%20linguist%20and%20speaking%20coach%20in%20his%20late%2040s%20with%20warm%20approachable%20expression%20and%20calm%20intelligent%20demeanor.%20Natural%20lighting%2C%20neutral%20soft%20background%2C%20relaxed%20professional%20posture.%20Wearing%20smart%20casual%20business%20attire%2C%20genuine%20smile%20showing%20expertise%20and%20approachability.%20Slightly%20desaturated%20colors%2C%20natural%20skin%20tones%2C%20authentic%20professional%20headshot%20style%20without%20heavy%20retouching.%20Real%20person%20aesthetic%20showing%20calm%20confidence%20and%20teaching%20experience%2C%20not%20corporate%20or%20posed.&width=600&height=700&seq=edbilities-team-tony-patterson-v1&orientation=portrait"
                    alt="Tony Patterson, Founder & Lead Coach"
                    className="w-full h-auto object-cover object-top"
                    style={{ minHeight: '500px' }}
                  />
                </div>
              </div>

              {/* Bio */}
              <div className="w-[65%] pt-8">
                <div className="mb-6">
                  <h3 className="font-sans text-3xl font-bold text-charcoal mb-2">Tony Patterson</h3>
                  <p className="font-sans text-lg text-teal font-semibold">Founder & Lead Coach</p>
                </div>

                <div className="space-y-5">
                  <p className="font-sans text-lg leading-relaxed text-charcoal">
                    Tony is a linguist and professional speaking coach who helps fluent non-native English speakers build a more natural, credible professional English accent. His work focuses on rhythm, flow, vocabulary choice, and habit-building—so professionals can communicate clearly and confidently in real-world conversations, presentations, and meetings.
                  </p>

                  <p className="font-sans text-lg leading-relaxed text-charcoal">
                    Tony's approach is practice-driven and highly interactive. Instead of teaching rules, he listens carefully, corrects patterns, and guides learners through repetition until new speaking habits feel natural and automatic.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Team Member 2 - Chris Mackey */}
          <div>
            <div className="flex items-start gap-16">
              {/* Bio - Left Side */}
              <div className="w-[65%] pt-8">
                <div className="mb-6">
                  <h3 className="font-sans text-3xl font-bold text-charcoal mb-2">Chris Mackey</h3>
                  <p className="font-sans text-lg text-teal font-semibold">Coach</p>
                </div>

                <div className="space-y-5">
                  <p className="font-sans text-lg leading-relaxed text-charcoal">
                    Chris works alongside Tony to support learners throughout the Edbilities program. He brings experience working with adult learners across cultures and learning styles, with a focus on helping people feel confident, engaged, and supported as they build new communication skills.
                  </p>

                  <p className="font-sans text-lg leading-relaxed text-charcoal">
                    Chris helps facilitate the learning experience, supports live sessions, and ensures the program stays practical, human, and accessible—especially for learners who have struggled with traditional language courses in the past.
                  </p>
                </div>
              </div>

              {/* Portrait - Right Side */}
              <div className="w-[35%] flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-gray-900/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20friendly%20male%20coach%20in%20his%20mid-30s%20with%20warm%20engaging%20expression%20and%20approachable%20confident%20demeanor.%20Natural%20lighting%2C%20neutral%20soft%20background%2C%20relaxed%20professional%20posture.%20Wearing%20smart%20casual%20business%20attire%2C%20genuine%20welcoming%20smile%20showing%20supportive%20teaching%20style.%20Slightly%20desaturated%20colors%2C%20natural%20skin%20tones%2C%20authentic%20professional%20headshot%20style%20without%20heavy%20retouching.%20Real%20person%20aesthetic%20showing%20calm%20confidence%20and%20facilitation%20experience%2C%20not%20corporate%20or%20posed.&width=600&height=700&seq=edbilities-team-chris-mackey-v1&orientation=portrait"
                    alt="Chris Mackey, Coach"
                    className="w-full h-auto object-cover object-top"
                    style={{ minHeight: '500px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special content for "Final CTA" section
  if (id === 'cta') {
    return (
      <section id={id} className="py-32 bg-gradient-to-b from-white to-warm-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <div className="flex items-center gap-20">
            {/* Left Column - Content */}
            <div className="w-[55%]">
              {/* Headline */}
              <div className="mb-12">
                <h2 className="font-serif text-5xl leading-tight text-charcoal mb-4">
                  Your credentials are real.
                </h2>
                <h2 className="font-serif text-5xl leading-tight text-charcoal">
                  Your English should reflect that.
                </h2>
              </div>

              {/* Supporting Copy */}
              <div className="mb-12">
                <p className="font-sans text-xl leading-relaxed text-warm-gray max-w-[520px]">
                  If you're ready to sound more clear, confident, and professional in the conversations that affect your income, start here.
                </p>
              </div>

              {/* CTA Button */}
              <div>
                <button
                  onClick={() => {
                    // Scroll to top or open enrollment
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-teal text-white px-12 py-5 rounded-xl text-lg font-semibold font-sans shadow-xl shadow-teal/20 hover:bg-teal/90 hover:shadow-2xl hover:shadow-teal/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Start Building Your Professional English
                </button>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="w-[45%]">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/10">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20confident%20diverse%20professional%20in%20their%20mid-30s%20sitting%20at%20a%20modern%20desk%20during%20a%20video%20call%20or%20presentation%2C%20speaking%20clearly%20and%20naturally%20with%20calm%20assured%20body%20language%20and%20genuine%20engaged%20expression.%20Natural%20office%20lighting%20with%20soft%20warm%20tones%2C%20slightly%20desaturated%20colors%2C%20neutral%20background.%20The%20person%20appears%20credible%2C%20articulate%2C%20and%20professionally%20confident%20while%20communicating.%20Authentic%20workplace%20moment%20showing%20clear%20professional%20communication%20and%20self-assured%20presence%20during%20an%20important%20conversation%20or%20meeting.&width=700&height=800&seq=edbilities-final-cta-confident-speaker-v1&orientation=portrait"
                  alt="Professional speaking with confidence and clarity"
                  className="w-full h-auto object-cover object-top"
                  style={{ minHeight: '600px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default placeholder for other sections
  return (
    <section id={id} className="py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-20">
        <div className="text-center">
          <h2 className="font-serif text-4xl text-charcoal mb-4">{title}</h2>
          <p className="font-sans text-lg text-warm-gray">This section will be created in the next step</p>
        </div>
      </div>
    </section>
  );
}
